import { useCallback, useRef } from 'react'
import { Vector2, Vector3 } from 'three'
import { useControlStore } from '@editor/stores'
import { useCanvasListener } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'

const useTriggerContextMenu = () => {
  const setContext = useControlStore(state => state.setContextMenu)
  const context = useControlStore(state => state.contextMenu)

  const openContextMenu = useCallback(
    (
      point: Vector3,
      elementId: string | null,
      actions: Record<string, unknown>,
      userData: Record<string, unknown>,
    ) => {
      setContext({ point, elementId, actions, userData })
    },
    [setContext],
  )

  const closeContextMenu = useCallback(() => {
    setContext(null)
  }, [setContext])

  const startPosition = useRef<Vector2>(new Vector2())
  const currentPosition = useRef<Vector2>(new Vector2())
  useCanvasListener('pointerdown', event => {
    startPosition.current = new Vector2(event.clientX, event.clientY)
  })
  useCanvasListener('mousemove', event => {
    currentPosition.current = new Vector2(event.clientX, event.clientY)
  })

  /**
   * Should generally be added as onPointerUp handler so it
   * is only triggered on right mouse button and when button is released
   */
  const openContextMenuViaEvent = useCallback(
    (event: ThreeEvent<MouseEvent>, onlyRightMouseBtn = true) => {
      if (startPosition.current.distanceTo(currentPosition.current) > 10) return
      if (onlyRightMouseBtn && event.button !== 2) return
      if (!event.object.visible) return

      // we need at least an empty contextMenuActions array to show the context
      // menu, otherwise we will assume that the element has no context menu actions
      // and hide the context menu
      const hasContextMenuActions = event.object && 'contextMenuActions' in event.object.userData

      event.stopPropagation()

      if (event.object && hasContextMenuActions) {
        const elementId = event.object?.name || null
        const actions = event.object?.userData.contextMenuActions || {}
        openContextMenu(event.point, elementId, actions, event.object?.userData || {})
      } else {
        closeContextMenu()
      }
    },
    [closeContextMenu, openContextMenu],
  )

  return { openContextMenu, closeContextMenu, context, openContextMenuViaEvent }
}

export default useTriggerContextMenu
